import React, {
	useState,
	useRef,
	useEffect,
	MutableRefObject,
	CSSProperties,
	useCallback,
} from "react";
import "./Property.css";

//Components
import DetailsList from "./details-list/DetailsList";
import ZoomHome from "../shared/widgets/zoom-home/ZoomHome";
import SearchFilter from "./search-filter/SearchFilter";
import SummaryPage from "./summary-page/SummaryPage";
import { Property } from "../../models/Property";
import MobileSummaryPage from "./summary-page/mobile-view/MobileSummaryPage";
import { useAuth } from "../../context/useAuth";

//Esri Imports
import WebMap from "@arcgis/core/WebMap";
import MapView from "@arcgis/core/views/MapView";
import Expand from "@arcgis/core/widgets/Expand";
import Search from "@arcgis/core/widgets/Search";
import Compass from "@arcgis/core/widgets/Compass";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Basemap from "@arcgis/core/Basemap";
import Extent from "@arcgis/core/geometry/Extent";
import { watch } from "@arcgis/core/core/reactiveUtils.js";
import { MobileViewNavigation, MobileMainContentTypes } from "./mobile-view-nav";
import { useScreenSize } from "../../context/useScreenSize";
import Loading from "../shared/loading/Loading";
import { useSearchParams } from "react-router-dom";
// TODO: Implement custom legend
// import CustomLegend from '../shared/widgets/custom-legend/CustomLegend';

interface iProps {
	webMapId?: string;
}

type StateGeometry = Record<string, __esri.Geometry>;
interface StateOption { value: string; label: string; };

function FindProperty(props: iProps) {
	// #region useState mgmt
	const [filterByMapExtent, setFilterByMapExtent] = useState(true);
	const [radiusFilterGeometry, setRadiusFilterGeometry] = useState<__esri.Polygon | null>(null);

	const [searchOpen, setSearchOpen] = useState(false);
	const [stateFilterGeometry, setStateFilterGeometry] = useState<__esri.Polygon | null>(null);
	const [mobileMainContent, setMobileMainContent] = useState<MobileMainContentTypes>(MobileMainContentTypes.MAP);
	const [stateOptions, setStateOptions] = useState<StateOption[]>([]);
	const stateGeometries: MutableRefObject<StateGeometry> = useRef({});
	const [regionOptions, setRegionOptions] = useState<Set<string>>(new Set());
	// #endregion

	const propertiesLayerRef: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();

	const zoomHomeWidget = useRef<HTMLDivElement>();

	const attachZoomHomeWidget = (node: HTMLDivElement | null) => {
		if (node && !zoomHomeWidget.current) {
			node.hidden = true;
			zoomHomeWidget.current = node;
		}
	};

	const [property, setProperty] = useState<Property>();
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		if (property) setSearchParams({ id: property.globalId });
	}, [property]);

	const mapUIInitialized = useRef(false);
	const mapDiv = useRef(null);
	const summaryMapDiv: MutableRefObject<HTMLDivElement | null> = useRef(null);

	const map = useRef(new WebMap({
		portalItem: {
			id: props.webMapId
		}
	}));

	const filter = useRef<React.ElementRef<typeof SearchFilter>>(null);
	const detailsList = useRef<React.ElementRef<typeof DetailsList>>(null);

	const extent = new Extent({
		xmin: -65,
		xmax: -125,
		ymax: 55,
		ymin: 25,
	});

	const view: React.MutableRefObject<__esri.MapView> = useRef(new MapView({
		map: map.current,
		extent: extent,
	}));

	if (!mapUIInitialized.current) {
		view.current.popupEnabled = false;
		view.current.ui.remove("zoom");
		const search = new Search({
			id: 'searchbar',
			view: view.current,
			locationEnabled: false,
			includeDefaultSources: false,
			searchAllEnabled: true
		});

		view.current.when(() => {
			const propertiesLayer = view.current.map.layers
				.find(layer => layer.title === 'Properties') as __esri.FeatureLayer;
			propertiesLayerRef.current = propertiesLayer;

			if (!search.includeDefaultSources) {
				let tempSearch = new Search();
				tempSearch.when(() => {
					search.sources.addMany(tempSearch.defaultSources);
					tempSearch.destroy();
				});
			}

			const statesLayer = view.current.map.layers
				.find(layer => layer.title === 'Properties') as __esri.FeatureLayer;
			generateStatesOptions(statesLayer);

			//hard coding region options for now
			setRegionOptions(new Set<string>(['CANADA', 'EAST COAST', 'GULF COAST', 'MIDWEST', 'ROCKY MOUNTAIN']))
			// generateRegionOptions(businessEntityLayer);

			// const legend = new Expand({
			//   expandIconClass: "esri-icon-legend",
			//   view: view.current,
			//   content: new CustomLegend({
			//     view: view.current,
			//     layerInfos: [
			//       {
			//         title: "Map Legend",
			//         layer: locationMarketingGroupLayer
			//       },
			//       {
			//         layer: watcoRailroadsLayer
			//       }
			//     ]
			//   }),
			//   expandTooltip: "Legend",
			//   expanded: window.innerWidth > 1080
			// });

			zoomHomeWidget.current!.hidden = false;
			view.current.ui.add([
				{
					component: zoomHomeWidget.current,
					position: "top-left",
					index: 2
				}
				/*{
					component: legend,
					position: "bottom-left",
					index: 0
				}*/
			]);
		});
		const compass = new Compass({ view: view.current });

		const topRightGroup = "top-right";
		// const layerList = new Expand({
		// 	view: view.current,
		// 	content: new LayerList({
		// 		view: view.current,
		// 		//selectionEnabled: true
		// 	}),
		// 	expandTooltip: "Layer List",
		// 	group: topRightGroup
		// });

		const basemaps = new Expand({
			view: view.current,
			content: new BasemapGallery({
				view: view.current,
				source: [Basemap.fromId("gray-vector"), Basemap.fromId("hybrid"), Basemap.fromId("streets-vector"), Basemap.fromId("terrain")]
			}),
			expandTooltip: "Basemaps",
			group: topRightGroup
		});

		view.current.ui.add([

			// top left section
			{
				component: search,
				position: "top-left",
				index: 0
			},
			{
				component: compass,
				position: "top-left",
				index: 1
			},

			// top-right-section
			// {Property
			//   component: searchButton,
			//   position: "top-right",
			//   index: 0
			// },
			// {
			// 	 component: layerList,
			// 	 position: "top-right",
			// 	 index: 1
			// },
			{
				component: basemaps,
				position: "top-right",
				index: 1
			}

		]);

		mapUIInitialized.current = true;
	}

	useEffect(() => {
		const w = watch(
			() => view.current.zoom,
			(zoom, oldZoom) => {
				if (zoom && oldZoom) {
					if (zoom === -1 || oldZoom === -1) return;

					if (zoom >= 13 && oldZoom < 13) {
						const bm = Basemap.fromId("hybrid");
						bm.when(() => {
							view.current.map.basemap = bm;
						});
						bm.load();

					} else if (zoom < 13 && oldZoom >= 13) {
						const bm = Basemap.fromId("gray-vector");
						bm.when(() => {
							view.current.map.basemap = bm;
						});
						bm.load();
					}
				}
			}
		);

		return () => {
			w.remove();
		};
	}, []);

	useEffect(() => {
		if (mapDiv.current) {
			view.current.container = mapDiv.current as any;
		}
	}, []);

	const stateAbbreviationMap: { [key: string]: string } = {
		"AL": "Alabama",
		"AK": "Alaska",
		"AZ": "Arizona",
		"AR": "Arkansas",
		"CA": "California",
		"CO": "Colorado",
		"CT": "Connecticut",
		"DE": "Delaware",
		"FL": "Florida",
		"GA": "Georgia",
		"HI": "Hawaii",
		"ID": "Idaho",
		"IL": "Illinois",
		"IN": "Indiana",
		"IA": "Iowa",
		"KS": "Kansas",
		"KY": "Kentucky",
		"LA": "Louisiana",
		"ME": "Maine",
		"MD": "Maryland",
		"MA": "Massachusetts",
		"MI": "Michigan",
		"MN": "Minnesota",
		"MS": "Mississippi",
		"MO": "Missouri",
		"MT": "Montana",
		"NE": "Nebraska",
		"NV": "Nevada",
		"NH": "New Hampshire",
		"NJ": "New Jersey",
		"NM": "New Mexico",
		"NY": "New York",
		"NC": "North Carolina",
		"ND": "North Dakota",
		"OH": "Ohio",
		"OK": "Oklahoma",
		"OR": "Oregon",
		"PA": "Pennsylvania",
		"RI": "Rhode Island",
		"SC": "South Carolina",
		"SD": "South Dakota",
		"TN": "Tennessee",
		"TX": "Texas",
		"UT": "Utah",
		"VT": "Vermont",
		"VA": "Virginia",
		"WA": "Washington",
		"WV": "West Virginia",
		"WI": "Wisconsin",
		"WY": "Wyoming"
	  };
	  
	  const generateStatesOptions = (statesLayer: __esri.FeatureLayer) => {	  
		if (statesLayer) {
		  statesLayer.queryFeatures({
			where: "1=1",
			returnGeometry: true,
			orderByFields: ["state ASC"],
			outFields: ["*"]
		  }).then(featureSet => {
	  
			const stateOptions: { value: string; label: string }[] = [];
			const stateAbbrToNameMap = new Map<string, string>();  // Hash Map for abbreviations
			const processedAbbreviations = new Set<string>();  // Set to track processed abbreviations
	  
			featureSet.features.forEach(feature => {
			  const stateAbbr = feature.attributes.state;
	  
			  // Skip if the state abbreviation has already been processed
			  if (processedAbbreviations.has(stateAbbr) || stateAbbr == null) {
				return;
			  }
	  
			  // Use the abbreviation to fetch the full name from the static map
			  const stateName = stateAbbreviationMap[stateAbbr] || stateAbbr;  // Fallback to abbreviation if not found
			  
			  // Add the abbreviation to the processed set
			  processedAbbreviations.add(stateAbbr);
	  
			  // Add to the map
			  stateAbbrToNameMap.set(stateAbbr, stateName);
	  
			  // Push the state to the stateOptions array
			  stateOptions.push({
				value: stateAbbr,
				label: stateName
			  });
	  
			  // Store geometry for each state abbreviation
			  stateGeometries.current[stateAbbr] = feature.geometry;
			});
	  
			setStateOptions(stateOptions);
		  }, console.error);
		}
	  };
	  
	  

	const onPropertyDetailSelect = useCallback((property: Property | undefined) => {
		setProperty(property);
		setSearchOpen(false);
	}, [setProperty]);

	const showMap = useCallback(() => {
		setMobileMainContent(MobileMainContentTypes.MAP);
	}, []);

	// #region Mobile Logic and CSS
	const { isSmallScreen, useMobileSummaryScreen: mobileSummaryPage } = useScreenSize();

	let mapStyle: CSSProperties = {};
	let detailsListStyle: CSSProperties = {};
	let mainViewStyle: CSSProperties = {};
	if (isSmallScreen) {
		if (mobileMainContent === 'map') {
			mapStyle = { width: '100%' };
			detailsListStyle = { display: 'none' };
		} else if (mobileMainContent === 'details') {
			mapStyle = { display: 'none' };
			detailsListStyle = { width: '100%' };
		} else if (mobileMainContent === 'filters') {
			mainViewStyle = { display: 'none' };
		}
	}
	// #endregion
	return (
		<div className="property">
			{isSmallScreen &&
				<MobileViewNavigation
					setMobileMainContent={setMobileMainContent}
					mobileMainContent={mobileMainContent}
				/>
			}
			<div style={mainViewStyle} className="main-view-container">
				<div id="property-map-container" style={mapStyle} className="map-container" ref={mapDiv}>
					<ZoomHome ref={attachZoomHomeWidget} view={view.current} extent={extent} />
				</div>
				<div style={detailsListStyle} className="right-side-panel">
					{!isSmallScreen &&
						<div className="panel-header">
							<button
								className='bg-orange no-border'
								onClick={() => setSearchOpen(!searchOpen)}>
								<span className="esri-icon-search"></span>
								<span>Search Properties</span>
							</button>
						</div>
					}
					<DetailsList
						ref={detailsList}
						view={view}
						filterByMapExtent={filterByMapExtent}
						stateFilterGeometry={stateFilterGeometry}
						radiusFilterGeometry={radiusFilterGeometry}
						filters={filter}
						stateOptions={stateOptions}
						setProperty={onPropertyDetailSelect}
						setMobileMainContent={setMobileMainContent}
						searchParams={searchParams}
					/>
				</div>
				{!mobileSummaryPage && property && propertiesLayerRef.current &&
					<SummaryPage
						map={map}
						property={property}
						setProperty={onPropertyDetailSelect}
						extent={extent}
						setSearchParams={setSearchParams}
					/>
				}
				{mobileSummaryPage && property && propertiesLayerRef.current &&
					<MobileSummaryPage
						map={map}
						property={property}
						setProperty={onPropertyDetailSelect}
						summaryMapDiv={summaryMapDiv}
						extent={extent}
					/>
				}
			</div>
			<SearchFilter ref={filter}
				view={view}
				detailsList={detailsList}
				stateOptions={stateOptions}
				isSmallScreen={isSmallScreen}
				visible={!isSmallScreen || mobileMainContent === 'filters'}
				setStateFilterGeometry={setStateFilterGeometry}
				stateGeometries={stateGeometries}
				regionOptions={regionOptions}
				extent={extent}
				displaySearchFilter={searchOpen}
				onClose={setSearchOpen}
				filterByMapExtent={setFilterByMapExtent}
				filterByMapExtentValue={filterByMapExtent}
				setMobileMainContent={setMobileMainContent}
				setRadiusFilterGeometry={setRadiusFilterGeometry}
				showMap={showMap} />
		</div>
	)
}

export default function PropertyWrapper({ webMapId }: iProps) {
	const { authInitialized } = useAuth();

	if (authInitialized) return <FindProperty webMapId={webMapId} />;
	else return (
		<div className="flex-row justify-center align-items-center w-100 h-100">
			<Loading />
		</div>
	);
}